import { useState } from "react";
import useData from "./useData";
import usePage from "./usePage";

const usePayTransaction = () => {
  const { executeAction } = useData();
  const { showMessage } = usePage();

  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);

  // Get transactions for a pay period
  const getByPayPeriod = async (payPeriodId) => {
    setLoading(true);
    try {
      const results = await executeAction({
        entityName: "PayTransaction",
        action: "getByPayPeriod",
        data: { payPeriodId },
      });

      if (results?.isSuccessful) {
        // Convert to array if it's an object with numeric keys
        const dataArray = Array.isArray(results.data)
          ? results.data
          : Object.values(results.data || {}).filter(
              (item) =>
                typeof item === "object" && item !== null && !item.isSuccessful,
            );
        setTransactions(dataArray);
        return dataArray;
      }
    } catch (err) {
      showMessage(err.message, "error");
    } finally {
      setLoading(false);
    }
  };

  // Get transactions for a user and pay period
  const getByUserAndPeriod = async (userId, payPeriodId) => {
    setLoading(true);
    try {
      const results = await executeAction({
        entityName: "PayTransaction",
        action: "getByUserAndPeriod",
        data: { userId, payPeriodId },
      });

      if (results?.isSuccessful) {
        setTransactions(results.data || []);
        return results.data;
      }
    } catch (err) {
      showMessage(err.message, "error");
    } finally {
      setLoading(false);
    }
  };

  // Process pay period - create transactions from calls
  const processPayPeriod = async (
    payPeriodId,
    startDate,
    endDate,
    processedBy,
  ) => {
    setLoading(true);
    try {
      const results = await executeAction({
        entityName: "PayTransaction",
        action: "processPayPeriod",
        data: { payPeriodId, startDate, endDate, processedBy },
      });

      if (results?.isSuccessful) {
        showMessage(
          `Processed ${
            results.data.processedCount
          } transactions totaling $${results.data.totalAmount.toFixed(2)}`,
          "success",
        );
        return results.data;
      } else {
        showMessage(
          results?.message || "Failed to process pay period",
          "error",
        );
      }
    } catch (err) {
      showMessage(err.message, "error");
    } finally {
      setLoading(false);
    }
  };

  // Get summary by user
  const getSummaryByUser = async (payPeriodId) => {
    setLoading(true);
    try {
      const results = await executeAction({
        entityName: "PayTransaction",
        action: "getSummaryByUser",
        data: { payPeriodId },
      });

      if (results?.isSuccessful) {
        return results.data || [];
      }
      return [];
    } catch (err) {
      showMessage(err.message, "error");
      return [];
    } finally {
      setLoading(false);
    }
  };

  // Get summary by transaction type
  const getSummaryByType = async (payPeriodId) => {
    setLoading(true);
    try {
      const results = await executeAction({
        entityName: "PayTransaction",
        action: "getSummaryByType",
        data: { payPeriodId },
      });

      if (results?.isSuccessful) {
        return results.data;
      }
    } catch (err) {
      showMessage(err.message, "error");
    } finally {
      setLoading(false);
    }
  };

  // Create manual adjustment
  const createAdjustment = async (
    payPeriodId,
    userId,
    amount,
    notes,
    createdBy,
  ) => {
    try {
      const results = await executeAction({
        entityName: "PayTransaction",
        action: "createAdjustment",
        data: { payPeriodId, userId, amount, notes, createdBy },
      });

      if (results?.isSuccessful) {
        showMessage("Adjustment created", "success");
        return results.data;
      } else {
        showMessage(results?.message || "Failed to create adjustment", "error");
      }
    } catch (err) {
      showMessage(err.message, "error");
    }
  };

  // Create mileage transaction
  const createMileage = async (
    payPeriodId,
    userId,
    distance,
    rate,
    notes,
    createdBy,
  ) => {
    try {
      const results = await executeAction({
        entityName: "PayTransaction",
        action: "createMileage",
        data: { payPeriodId, userId, distance, rate, notes, createdBy },
      });

      if (results?.isSuccessful) {
        showMessage("Mileage transaction created", "success");
        return results.data;
      } else {
        showMessage(
          results?.message || "Failed to create mileage transaction",
          "error",
        );
      }
    } catch (err) {
      showMessage(err.message, "error");
    }
  };

  // Preview payroll for the open pay period
  const previewPayroll = async () => {
    setLoading(true);
    try {
      const results = await executeAction({
        entityName: "PayTransaction",
        action: "previewPayroll",
        data: {},
      });

      if (results && results.payPeriod) {
        showMessage("Payroll preview loaded", "success");
        return results;
      } else {
        console.warn("Preview payroll returned empty:", results);
        showMessage(results?.message || "Failed to preview payroll", "error");
        return null;
      }
    } catch (err) {
      console.error("[previewPayroll] Error:", err);
      showMessage(err.message, "error");
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Apply payroll - create transactions from eligible calls and expenses
  const applyPayroll = async (processedBy) => {
    setLoading(true);
    try {
      const results = await executeAction({
        entityName: "PayTransaction",
        action: "applyPayroll",
        data: { processedBy },
      });

      if (results?.isSuccessful) {
        showMessage(
          `Payroll applied: ${
            results.data.summary.totalTransactions
          } transactions created${
            results.data.summary.errorTransactions > 0
              ? ` (${results.data.summary.errorTransactions} with errors)`
              : ""
          }`,
          "success",
        );
        return results.data;
      } else {
        showMessage(results?.message || "Failed to apply payroll", "error");
      }
    } catch (err) {
      showMessage(err.message, "error");
    } finally {
      setLoading(false);
    }
  };

  // Unassign a transaction (for disputes)
  const unassignTransaction = async (transactionId, reason, unassignedBy) => {
    try {
      const results = await executeAction({
        entityName: "PayTransaction",
        action: "unassignTransaction",
        data: { transactionId, reason, unassignedBy },
      });

      if (results?.isSuccessful) {
        showMessage("Transaction unassigned successfully", "success");
        return results.data;
      } else {
        showMessage(
          results?.message || "Failed to unassign transaction",
          "error",
        );
      }
    } catch (err) {
      showMessage(err.message, "error");
    }
  };

  // Unlock a call for editing (removes lock from call before opening editor)
  const unlockCallForEdit = async (transactionId) => {
    try {
      const results = await executeAction({
        entityName: "PayTransaction",
        action: "recalculateTransaction",
        data: { transactionId },
      });

      if (results?.isSuccessful) {
        return results.data;
      } else {
        showMessage(
          results?.message || "Failed to unlock call for editing",
          "error",
        );
        return null;
      }
    } catch (err) {
      showMessage(err.message, "error");
      return null;
    }
  };

  // Finalize call edit - recalculate transaction and re-lock call
  const finalizeCallEdit = async (transactionId) => {
    try {
      const results = await executeAction({
        entityName: "PayTransaction",
        action: "finalizeRecalculation",
        data: { transactionId },
      });

      if (results?.isSuccessful) {
        showMessage("Call updated and transaction recalculated", "success");
        return results.data;
      } else {
        showMessage(
          results?.message || "Failed to finalize call edit",
          "error",
        );
        return null;
      }
    } catch (err) {
      showMessage(err.message, "error");
      return null;
    }
  };

  // Remove a call from the pay period (deletes related transactions and unlocks the call)
  const removeCallFromPayPeriod = async (callId, payPeriodId, removedBy) => {
    try {
      const results = await executeAction({
        entityName: "PayTransaction",
        action: "removeCallFromPayPeriod",
        data: { callId, payPeriodId, removedBy },
      });

      if (results?.isSuccessful) {
        showMessage("Call removed from pay period", "success");
        return results.data;
      } else {
        showMessage(
          results?.message || "Failed to remove call from pay period",
          "error",
        );
        return null;
      }
    } catch (err) {
      showMessage(err.message, "error");
      return null;
    }
  };

  // Get changed calls since last payroll processing
  const getChangedCalls = async (payPeriodId) => {
    setLoading(true);
    try {
      const results = await executeAction({
        entityName: "PayTransaction",
        action: "getChangedCalls",
        data: { payPeriodId },
      });

      if (results?.isSuccessful) {
        return results.data;
      } else {
        showMessage(results?.message || "Failed to get changed calls", "error");
        return null;
      }
    } catch (err) {
      showMessage(err.message, "error");
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Reprocess pay period (smart or full)
  const reprocessPayPeriod = async (
    payPeriodId,
    fullReprocess = false,
    processedBy,
  ) => {
    setLoading(true);
    try {
      const results = await executeAction({
        entityName: "PayTransaction",
        action: "reprocessPayPeriod",
        data: { payPeriodId, fullReprocess, processedBy },
      });

      if (results?.isSuccessful) {
        // No toast - the PayPeriodProcessing component will show a dialog
        // when the worker completes and polling detects the status change
        return results.data;
      } else {
        showMessage(
          results?.message || "Failed to reprocess pay period",
          "error",
        );
        return null;
      }
    } catch (err) {
      showMessage(err.message, "error");
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Close pay period
  const closePayPeriod = async (payPeriodId) => {
    setLoading(true);
    try {
      const results = await executeAction({
        entityName: "PayTransaction",
        action: "closePayPeriod",
        data: { payPeriodId },
      });

      if (results?.isSuccessful) {
        // Now returns queued status instead of processing results
        showMessage(
          results.data.message ||
            "Pay period close queued. You will receive an email when complete.",
          "success",
        );
        return results.data;
      } else {
        showMessage(results?.message || "Failed to close pay period", "error");
        return null;
      }
    } catch (err) {
      showMessage(err.message, "error");
      return null;
    } finally {
      setLoading(false);
    }
  };

  return {
    transactions,
    loading,
    getByPayPeriod,
    getByUserAndPeriod,
    processPayPeriod,
    getSummaryByUser,
    getSummaryByType,
    createAdjustment,
    createMileage,
    previewPayroll,
    applyPayroll,
    unassignTransaction,
    unlockCallForEdit,
    finalizeCallEdit,
    removeCallFromPayPeriod,
    getChangedCalls,
    reprocessPayPeriod,
    closePayPeriod,
  };
};

export default usePayTransaction;
